import { useState, useCallback } from 'react';
import {
  DEFAULT_FORM_DATA,
  AI_PROVIDER_MODELS,
  normalizeProviderType,
} from '../../../../config/constants';

/**
 * Custom hook for managing template form state
 *
 * Handles:
 * - Form data state
 * - Tag management (add, remove)
 * - Input change handlers
 * - Model configuration changes with max_tokens sync
 *
 * @param {Object} initialFormData - Initial form data
 * @returns {Object} Form state and handlers
 */
export const useTemplateForm = (initialFormData = DEFAULT_FORM_DATA) => {
  const [formData, setFormData] = useState(initialFormData);
  const [newTag, setNewTag] = useState('');

  // Handle form input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle model config changes with max_tokens sync
  const handleModelConfigChange = useCallback((field, value) => {
    setFormData(prev => {
      const updatedConfig = {
        ...prev.model_config,
        [field]: value,
      };

      // Sync max_tokens when model_name changes
      if (field === 'model_name' && prev.model_config.provider) {
        const provider = normalizeProviderType(prev.model_config.provider);
        const modelName = value;

        // Get max_tokens from AI_PROVIDER_MODELS constants
        const providerModels = AI_PROVIDER_MODELS[provider];
        if (providerModels?.modelConfig?.[modelName]?.max_tokens) {
          updatedConfig.max_tokens =
            providerModels.modelConfig[modelName].max_tokens;
        }
      }

      // Sync max_tokens when provider changes (if model_name is already set)
      if (field === 'provider' && prev.model_config.model_name) {
        const provider = normalizeProviderType(value);
        const modelName = prev.model_config.model_name;

        const providerModels = AI_PROVIDER_MODELS[provider];
        if (providerModels?.modelConfig?.[modelName]?.max_tokens) {
          updatedConfig.max_tokens =
            providerModels.modelConfig[modelName].max_tokens;
        }
      }

      return {
        ...prev,
        model_config: updatedConfig,
      };
    });
  }, []);

  // Add tag
  const handleAddTag = useCallback(() => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  }, [newTag, formData.tags]);

  // Remove tag - ensures exact match and creates new array reference for React
  const handleRemoveTag = useCallback(tagToRemove => {
    setFormData(prev => {
      // Filter out all instances of the tag (though duplicates should be prevented)
      const filteredTags = prev.tags.filter(
        tag => String(tag).trim() !== String(tagToRemove).trim()
      );
      return {
        ...prev,
        tags: filteredTags,
      };
    });
  }, []);

  // Handle tag input key press
  const handleTagKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  return {
    formData,
    setFormData,
    newTag,
    setNewTag,
    handleInputChange,
    handleModelConfigChange,
    handleAddTag,
    handleRemoveTag,
    handleTagKeyPress,
  };
};
